/**
 * <newsletter-form> — progressive enhancement for snippets/newsletter-form.liquid.
 * Posts the Shopify customer form with fetch and shows success / error inline.
 * Without JS the form posts normally and Shopify re-renders the page.
 * Fires `newsletter:subscribed` on document on success (the popup listens).
 */
if (!customElements.get('newsletter-form')) {
  customElements.define(
    'newsletter-form',
    class NewsletterForm extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        if (!this.form || !window.fetch) return;
        this.fields = this.querySelector('[data-fields]');
        this.success = this.querySelector('[data-success]');
        this.errors = this.querySelector('[data-errors]');
        this.errorText = this.querySelector('[data-error-text]');
        this.button = this.form.querySelector('[type="submit"]');
        this.spinner = this.button?.querySelector('.loading__spinner');
        this.defaultError = this.errorText?.textContent.trim() || '';
        this.form.addEventListener('submit', this.onSubmit.bind(this));
      }

      setLoading(state) {
        if (!this.button) return;
        this.button.classList.toggle('loading', state);
        this.button.toggleAttribute('aria-disabled', state);
        this.spinner?.classList.toggle('hidden', !state);
      }

      showError(message) {
        if (!this.errors) return;
        if (this.errorText) this.errorText.textContent = message || this.defaultError;
        this.errors.hidden = false;
        this.form.querySelector('input[type="email"]')?.setAttribute('aria-invalid', 'true');
      }

      showSuccess() {
        this.classList.add('is-success');
        if (this.fields) this.fields.hidden = true;
        if (this.success) {
          this.success.hidden = false;
          this.success.focus();
        }
        document.dispatchEvent(
          new CustomEvent('newsletter:subscribed', { bubbles: true, detail: { id: this.form.id } })
        );
      }

      async onSubmit(event) {
        // Native validation (required, type=email, consent) has already passed when submit fires.
        event.preventDefault();
        this.handoff = false;
        if (this.errors) this.errors.hidden = true;
        this.form.querySelector('input[type="email"]')?.removeAttribute('aria-invalid');
        this.setLoading(true);

        try {
          const response = await fetch(this.form.action, {
            method: 'POST',
            body: new FormData(this.form),
            headers: { Accept: 'text/html' },
            redirect: 'follow',
          });
          const finalUrl = new URL(response.url, window.location.href);

          // Shopify bot challenge: hand the user over to the native flow.
          if (finalUrl.pathname.includes('/challenge')) {
            this.handoff = true;
            this.form.submit();
            return;
          }

          const posted =
            finalUrl.searchParams.get('customer_posted') === 'true' ||
            finalUrl.searchParams.get('contact_posted') === 'true';

          if (response.ok && posted) {
            this.showSuccess();
            return;
          }

          // Re-rendered page: lift the server error for this form if there is one.
          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const serverError = doc.querySelector(`#${CSS.escape(this.form.id)} [data-error-text]`);
          const serverErrorsBox = doc.querySelector(`#${CSS.escape(this.form.id)} [data-errors]`);
          if (serverError && serverErrorsBox && !serverErrorsBox.hasAttribute('hidden')) {
            this.showError(serverError.textContent.trim());
          } else if (response.ok && doc.querySelector(`#${CSS.escape(this.form.id)} .is-success, #${CSS.escape(this.form.id)} [data-success]:not([hidden])`)) {
            this.showSuccess();
          } else {
            // Unreadable response (bot challenge, security rejection). Hand the
            // form to the native flow instead of claiming a failure we cannot
            // prove: a real navigation clears the challenge and succeeds.
            this.handoff = true;
            this.form.submit();
          }
        } catch (error) {
          this.showError();
        } finally {
          if (!this.handoff) this.setLoading(false);
        }
      }
    }
  );
}
